import { useEffect, useState } from "react";
import axios from "axios";
import { Pencil, Save } from "lucide-react";
import { toast } from "sonner";
import { companyRoleLabels } from "@/utils/companyLabels";
import {
  CustomerSupervisorSelect,
  ProjectManagerDropdown,
} from "@/components/ProjectAssignmentControls";
import { TypedDateInput } from "@/components/TypedDateInput";
import { createConflictHandler, attachExpectedVersion } from "@/lib/conflictHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

const editableFields = [
  "project_code",
  "name",
  "client",
  "city",
  "due_date",
  "scheduled_date",
  "customer_supervisor",
  "work_scope",
  "remaining_work",
  "notes",
  "project_manager_user_ids",
];

const lifecycleLabels = {
  blocked: "Roadblocked",
  complete: "Complete",
  closed: "Closed",
  in_progress: "In Progress",
  not_started: "Not Started",
  canceled: "Canceled",
};

const createDraft = (project, canAccessBilling) => {
  const nextDraft = editableFields.reduce((draft, field) => {
    draft[field] = project[field] ?? "";
    return draft;
  }, {});

  if (canAccessBilling) {
    nextDraft.what_is_invoiced = project.what_is_invoiced ?? "";
    nextDraft.project_printed = Boolean(project.project_printed);
  }

  return nextDraft;
};

export const ProjectEditPanel = ({
  canAccessBilling,
  companyContext,
  onSaved,
  project,
}) => {
  const companyLabels = companyRoleLabels(companyContext);
  const [draft, setDraft] = useState(() =>
    createDraft(project, canAccessBilling),
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingStage, setIsChangingStage] = useState(false);
  const [nextLifecycleState, setNextLifecycleState] = useState("");
  const [blockerReason, setBlockerReason] = useState("");
  const [completionNotes, setCompletionNotes] = useState("");
  const [users, setUsers] = useState([]);

  useEffect(() => {
    setDraft(createDraft(project, canAccessBilling));
    setNextLifecycleState("");
    setBlockerReason("");
  }, [canAccessBilling, project]);

  useEffect(() => { if (isOpen) axios.get(`${API}/assignable-users`).then((response) => setUsers(response.data.users)); }, [isOpen]);

  const updateField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const cancel = () => {
    setDraft(createDraft(project, canAccessBilling));
    setIsOpen(false);
  };

  const save = async (event) => {
    event.preventDefault();
    const changes = Object.entries(draft).reduce((nextChanges, [field, value]) => {
      if (value !== (project[field] ?? "")) {
        nextChanges[field] = value;
      }
      return nextChanges;
    }, {});

    if (!Object.keys(changes).length) {
      toast.message("No project changes to save.");
      setIsOpen(false);
      return;
    }

    setIsSaving(true);

    const handleConflict = createConflictHandler(onSaved);
    try {
      await axios.patch(
        `${API}/projects/${project.id}`,
        attachExpectedVersion(changes, project),
      );
      toast.success("Project information saved.");
      setIsOpen(false);
      await onSaved();
    } catch (error) {
      if (handleConflict(error)) {
        setIsOpen(false);
      } else {
        toast.error(
          error.response?.data?.detail || "Project information could not be saved.",
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const changeOfficialStage = async () => {
    if (!nextLifecycleState) return;
    setIsChangingStage(true);

    try {
      if (nextLifecycleState === "blocked") {
        await axios.post(`${API}/projects/${project.id}/blocker/create`, {
          reason: blockerReason.trim(),
        });
      } else if (["complete", "closed"].includes(nextLifecycleState)) {
        await axios.post(`${API}/projects/${project.id}/lifecycle`, {
          lifecycle_state: nextLifecycleState,
          notes: completionNotes,
        });
      } else if (project.operational_blocker) {
        await axios.post(`${API}/projects/${project.id}/blocker/resolve`, {
          lifecycle_state: nextLifecycleState,
        });
      } else {
        await axios.post(`${API}/projects/${project.id}/lifecycle${canAccessBilling ? "/override" : ""}`, {
          lifecycle_state: nextLifecycleState,
          reason: "",
        });
      }
      toast.success("Project Stage updated.");
      setNextLifecycleState("");
      await onSaved();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Project stage could not be updated.",
      );
    } finally {
      setIsChangingStage(false);
    }
  };

  const lifecycleChoices = canAccessBilling
    ? ["not_started", "in_progress", "blocked", ...( ["in_progress", "blocked"].includes(project.lifecycle_state) ? ["complete"] : []), ...(project.lifecycle_state === "complete" ? ["closed"] : [])]
    : project.available_lifecycle_transitions || [];

  return (
    <section className="project-edit-panel" data-testid="project-edit-panel">
      <div className="project-edit-panel-heading">
        <div>
          <span className="operations-kicker">Role-approved project update</span>
          <h2>Project information</h2>
        </div>
        <button
          className="board-refresh-button"
          data-testid="project-edit-toggle-button"
          disabled={isSaving}
          onClick={() => setIsOpen((current) => !current)}
          type="button"
        >
          <Pencil size={15} />
          {isOpen ? "Close editor" : "Edit project"}
        </button>
      </div>

      {isOpen && (
        <form
          className="project-update-form"
          data-testid="project-update-form"
          onSubmit={save}
        >
          <label htmlFor="project-update-code">
            Project number
            <input
              data-testid="project-update-code-input"
              id="project-update-code"
              onChange={(event) => updateField("project_code", event.target.value)}
              required
              value={draft.project_code}
            />
          </label>
          <label htmlFor="project-update-name">
            Project Name
            <input
              data-testid="project-update-name-input"
              id="project-update-name"
              onChange={(event) => updateField("name", event.target.value)}
              required
              value={draft.name}
            />
          </label>
          <label htmlFor="project-update-client">
            Customer
            <input
              data-testid="project-update-client-input"
              id="project-update-client"
              onChange={(event) => updateField("client", event.target.value)}
              required
              value={draft.client}
            />
          </label>
          {canAccessBilling && <label htmlFor="project-update-city">
            City
            <input
              data-testid="project-update-city-input"
              id="project-update-city"
              onChange={(event) => updateField("city", event.target.value)}
              value={draft.city}
            />
          </label>}
          <div className="project-update-assignment-field project-update-form-wide">
            <span>Assigned {companyLabels.projectManager}s</span>
            <ProjectManagerDropdown
              onChange={(value) => updateField("project_manager_user_ids", value)}
              testId="project-manager-assignment"
              users={users}
              value={draft.project_manager_user_ids || []}
            />
          </div>
          <div
            className="project-update-stage-control"
            data-testid="project-update-stage-control"
          >
            <span>Project Stage</span>
            <strong data-testid="project-current-lifecycle-state">
              {lifecycleLabels[project.lifecycle_state] || "Managed by closeout"}
            </strong>
            {lifecycleChoices.length ? (
              <div className="project-update-stage-actions">
                <select
                  data-testid="project-next-lifecycle-select"
                  onChange={(event) => setNextLifecycleState(event.target.value)}
                  value={nextLifecycleState}
                >
                  <option value="">Select next stage</option>
                  {lifecycleChoices.map((state) => (
                    <option key={state} value={state}>
                      {lifecycleLabels[state]}
                    </option>
                  ))}
                </select>
                {nextLifecycleState === "blocked" && (
                  <input
                    data-testid="project-blocker-reason-input"
                    onChange={(event) => setBlockerReason(event.target.value)}
                    placeholder="Optional Roadblocked reason"
                    value={blockerReason}
                  />
                )}
                {["complete", "closed"].includes(nextLifecycleState) && (
                  <textarea
                    data-testid="project-completion-notes-input"
                    onChange={(event) => setCompletionNotes(event.target.value)}
                    placeholder={nextLifecycleState === "closed" ? "Optional closure notes" : "Optional completion notes"}
                    value={completionNotes}
                  />
                )}
                <button
                  className="board-refresh-button"
                  data-testid="project-update-stage-button"
                  disabled={
                    !nextLifecycleState ||
                    isChangingStage ||
                    isSaving
                  }
                  onClick={changeOfficialStage}
                  type="button"
                >
                  {isChangingStage ? "Updating…" : "Update stage"}
                </button>
              </div>
            ) : (
              <small data-testid="project-stage-dedicated-workflow-note">
                This stage is managed by the dedicated project action workflow.
              </small>
            )}
          </div>
          <label htmlFor="project-update-due-date">
            Due Date
            <TypedDateInput
              data-testid="project-update-due-date-input"
              id="project-update-due-date"
              onChange={(value) => updateField("due_date", value)}
              value={draft.due_date}
            />
          </label>
          <label htmlFor="project-update-scheduled-date">
            Scheduled date
            <TypedDateInput
              data-testid="project-update-scheduled-date-input"
              id="project-update-scheduled-date"
              onChange={(value) => updateField("scheduled_date", value)}
              value={draft.scheduled_date}
            />
          </label>
          {canAccessBilling && (
            <label htmlFor="project-update-prints-required">
              Project Printed?
              <select
                data-testid="project-update-project-printed-select"
                id="project-update-project-printed"
                onChange={(event) =>
                  updateField("project_printed", event.target.value === "true")
                }
                value={String(Boolean(draft.project_printed))}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </label>
          )}
          <label>
            Customer Supervisor
            <CustomerSupervisorSelect
              onChange={(value) => updateField("customer_supervisor", value)}
              testId="project-update-customer-supervisor-input"
              value={draft.customer_supervisor}
            />
          </label>
          <label className="project-update-form-wide" htmlFor="project-update-scope">
            Work scope
            <textarea
              data-testid="project-update-scope-input"
              id="project-update-scope"
              onChange={(event) => updateField("work_scope", event.target.value)}
              value={draft.work_scope}
            />
          </label>
          <label className="project-update-form-wide" htmlFor="project-update-remaining-work">
            What is left of work
            <textarea
              data-testid="project-update-remaining-work-input"
              id="project-update-remaining-work"
              onChange={(event) =>
                updateField("remaining_work", event.target.value)
              }
              value={draft.remaining_work}
            />
          </label>
          {canAccessBilling && (
            <label className="project-update-form-wide" htmlFor="project-update-invoiced-work">
              What is invoiced
              <textarea
                data-testid="project-update-invoiced-work-input"
                id="project-update-invoiced-work"
                onChange={(event) =>
                  updateField("what_is_invoiced", event.target.value)
                }
                value={draft.what_is_invoiced}
              />
            </label>
          )}
          <label className="project-update-form-wide" htmlFor="project-update-notes">
            Project notes
            <textarea
              data-testid="project-update-notes-input"
              id="project-update-notes"
              onChange={(event) => updateField("notes", event.target.value)}
              value={draft.notes}
            />
          </label>
          <div className="project-update-form-actions">
            <button
              className="board-refresh-button"
              data-testid="project-update-cancel-button"
              disabled={isSaving}
              onClick={cancel}
              type="button"
            >
              Cancel
            </button>
            <button
              className="invoice-primary-button"
              data-testid="project-update-save-button"
              disabled={isSaving}
              type="submit"
            >
              <Save size={15} />
              {isSaving ? "Saving…" : "Save project update"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};